import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import type { EditorState, LexicalEditor } from 'lexical';
import { $setSelection, SKIP_DOM_SELECTION_TAG } from 'lexical';
import { useCallback, useEffect, useImperativeHandle, useRef } from 'react';

import { useMultiLanguage } from '../features/multi-language/MultiLanguageContext';
import { $getContent } from '../serialization/getContent';
import { $replaceContent } from '../serialization/replaceContent';
import { useEditorChrome } from './context/EditorChromeContext';
import { useEditorProps } from './context/EditorPropsContext';
import { useFeatureEnabled } from './context/FeaturesContext';
import { $insertHtmlAtCaret, $insertTextAtCaret } from './insertAtCaret';
import { CONTENT_LOAD_TAG } from './updateTags';

/**
 * Owns the editor's value in both directions — the only place content is loaded or serialised.
 * - Loads once on mount: the host's `initialValue`, or the current language's text with multi-language.
 * - Later `initialValue` changes are ignored (it's a seed); a language switch swaps the content.
 * - Serialises once per change, reporting to `onChange` and, with multi-language, the translations.
 * - Never loads while collaborating: the shared Yjs document is the source of truth.
 * - Exposes the host's `ref` handle (`PbkLexicalInputHandle`); `setContent` / `clear` replace only the
 *   current language with multi-language, and only warn while collaborating. The inserts are ordinary
 *   edits at the caret, so they also work while collaborating.
 */
export const ValuePlugin = () => {
  const [editor] = useLexicalComposerContext();
  const { initialValue, mode, onChange, ref } = useEditorProps();
  const collaborating = useFeatureEnabled('collaboration');
  const multiLanguage = useMultiLanguage();
  const { focused } = useEditorChrome();
  // Read, not a dependency: focusing or blurring alone must not reload the content
  const focusedRef = useRef(focused);
  focusedRef.current = focused;

  const incoming = multiLanguage ? multiLanguage.currentValue : initialValue;
  // What the editor holds, so our own change echoed back by the host isn't loaded in again
  const held = useRef<string | null>(null);

  const load = useCallback(
    (value: string, firstLoad: boolean) => {
      held.current = value;
      // A language picked with the pills keeps the caret; on mount, the reset to the UI language on blur
      // or a `setContent` while inactive, nothing may focus
      const quiet = firstLoad || !focusedRef.current;

      editor.update(
        () => {
          $replaceContent(editor, value, mode);
          // `$replaceContent` selects the root; flushing that to the DOM would focus the editor
          // (stealing focus and opening the toolbar). The tag covers this update, clearing the
          // selection covers the later commit that attaches the content element on mount.
          if (quiet) $setSelection(null);
        },
        { tag: quiet ? [CONTENT_LOAD_TAG, SKIP_DOM_SELECTION_TAG] : CONTENT_LOAD_TAG },
      );
    },
    [editor, mode],
  );

  useEffect(() => {
    if (collaborating) return;
    const firstLoad = held.current === null;
    if (!firstLoad && (!multiLanguage || incoming === held.current)) return;
    load(incoming, firstLoad);
  }, [collaborating, incoming, load, multiLanguage]);

  useImperativeHandle(ref, () => {
    const setContent = (value: string) => {
      if (collaborating) {
        console.warn('PbkLexicalInput: setContent / clear are ignored while collaborating; the shared document is the source of truth.');
        return;
      }
      load(value, false);
    };

    // Typed-in edits, so not while disabled; the editor is focused after, with the caret past the insert
    const insert = (fn: () => void) => {
      if (!editor.isEditable()) return;
      // Focused once committed: `editor.focus()` skips the DOM focus while an update is pending
      editor.update(fn, { onUpdate: () => editor.focus() });
    };

    return {
      setContent,
      clear: () => setContent(''),
      focus: () => editor.focus(),
      insertText: (text: string) => insert(() => $insertTextAtCaret(text)),
      insertHtml: (html: string) => insert(() => $insertHtmlAtCaret(editor, html, mode)),
    };
  }, [collaborating, editor, load, mode]);

  const handleChange = (editorState: EditorState, activeEditor: LexicalEditor) => {
    // The HTML export needs an active editor, hence `{ editor }`
    const value = editorState.read(() => $getContent(activeEditor, mode), { editor: activeEditor });
    held.current = value;
    onChange?.(value);
    multiLanguage?.setCurrentValue(value);
  };

  return <OnChangePlugin onChange={handleChange} ignoreSelectionChange />;
};
